  
  // Table_1.jsx : 주문서 작성
  
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "../../styles/Table.css";
import { toast } from "react-toastify";

const Table_1 = ({
  slideOpen,
  list,
  orderItemList,
  setProductQuery,
  productQuery,
  requestText,
  onRequestBoxChange,
  handleAddOrderItem,
  selectedProductCode,
  setSelectedProductCode,
  currentPage,
  setCurrentPage,
  columnWidths = ["80px", "200px", "120px", "80px", "60px", "100px"],
  columnAligns = ["center", "center", "center", "right", "center", "center"],
}) => {
  const [focusedProductCode, setFocusedProductCode] = useState(null);

  const tableContainerRef = useRef(null);
  const selectedRowRef = useRef(null);
  const shouldFocusNextInputRef = useRef(false);
  const [quantities, setQuantities] = useState({});
  
  const itemsPerPage = 8;
  const pagesPerGroup = 5;

  const validCurrentPage = Math.max(1, currentPage || 1);

  // 선택 행이 바뀔 때 스크롤로 화면에 보이도록 수동 계산
  useEffect(() => {
    const row = selectedRowRef.current;
    const container = tableContainerRef.current;
    if (!row || !container) return;

    // sticky 헤더 높이 측정
    const thead = container.querySelector("thead");
    const theadHeight = thead ? thead.offsetHeight : 0;

    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    const visibleTop = container.scrollTop + theadHeight;
    const visibleBottom = container.scrollTop + container.clientHeight;

    if (rowTop < visibleTop) {
      container.scrollTop = rowTop - theadHeight;
    } else if (rowBottom > visibleBottom) {
      container.scrollTop = rowBottom - container.clientHeight;
    }
  }, [selectedProductCode]);

  // --- 순서 변경 시작 ---
  // 1. 검색 키워드 및 필터링된 리스트 계산 (먼저 수행)
  const keyword = useMemo(
    () => (productQuery || "").trim().toLowerCase(),
    [productQuery]
  );

  const filteredList = useMemo(() => {
    return list.filter((item) => {
      if (!keyword) return true;
      const fieldsToSearch = [
        item?.ProductCode || "",
        item?.AliasName || "",
        item?.ModelName || "",
      ];
      return fieldsToSearch.some((field) =>
        field.toLowerCase().includes(keyword)
      );
    });
  }, [keyword, list]);

  // 2. 필터링된 리스트를 기반으로 총 페이지 수 계산
  const totalPages = Math.ceil(
    (filteredList?.length || 0) / itemsPerPage
  );

  const totalGroups = Math.ceil(totalPages / pagesPerGroup);
  const pageGroup = Math.floor((validCurrentPage - 1) / pagesPerGroup);
  
  const startPage = pageGroup * pagesPerGroup + 1;
  const endPage = Math.min(startPage + pagesPerGroup - 1, totalPages);

  const indexOfLastItem = validCurrentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = Array.isArray(filteredList)
    ? filteredList.slice(indexOfFirstItem, indexOfLastItem)
    : [];
  // --- 순서 변경 끝 ---
  
  const goToPage = (number) => setCurrentPage && setCurrentPage(number);
  
  const handlePrevGroup = () => {
    if (pageGroup > 0 && setCurrentPage) {
      setCurrentPage((pageGroup - 1) * pagesPerGroup + 1);
    }
  };
  
  const handleNextGroup = () => {
    if (pageGroup < totalGroups - 1 && setCurrentPage) {
      setCurrentPage((pageGroup + 1) * pagesPerGroup + 1);
    }
  };

  const handleOrderBillRowClick = useCallback((data) => {
    // 이미 선택된 동일한 행을 마우스로 다시 클릭한 경우 (React 상태변경이 일어나지 않음)
    if (selectedProductCode === data.ProductCode) {
      const newRowInput = tableContainerRef.current?.querySelector(".selected-row input");
      if (newRowInput) newRowInput.focus();
    } else {
      // 다른 행을 새롭게 선택한 경우 (렌더링 후 useEffect가 작동하도록 예약)
      shouldFocusNextInputRef.current = true; 
      setSelectedProductCode(data.ProductCode);
    }
  }, [selectedProductCode, setSelectedProductCode]);

  // Up/Down 키 핸들러
  const handleKeyDown = useCallback(
    (e) => {
      // 텍스트 선택 등 좌우 화살표는 허용하되, 상하 화살표는 행 이동에 작동하도록 방어 해제
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      e.preventDefault();

      const listArr = Array.isArray(filteredList) ? filteredList : [];
      const currentIndex = listArr.findIndex(
        (item) => item.ProductCode === selectedProductCode
      );

      let nextIndex;
      if (currentIndex === -1) {
        nextIndex = 0;
      } else if (e.key === "ArrowUp") {
        nextIndex = Math.max(0, currentIndex - 1);
      } else {
        nextIndex = Math.min(listArr.length - 1, currentIndex + 1);
      }

      if (nextIndex === currentIndex && currentIndex !== -1) return;

      const nextItem = listArr[nextIndex];
      if (!nextItem) return;

      // 페이지 전환 처리
      const nextPage = Math.floor(nextIndex / itemsPerPage) + 1;
      if (nextPage !== validCurrentPage && setCurrentPage) {
        setCurrentPage(nextPage);
      }

      handleOrderBillRowClick(nextItem);
    },
    [filteredList, selectedProductCode, validCurrentPage, itemsPerPage, setCurrentPage, handleOrderBillRowClick]
  );

  // 렌더링(페이지 전환 또는 행 변경) 후 포커스를 복구
  useEffect(() => {
    if (shouldFocusNextInputRef.current) {
      shouldFocusNextInputRef.current = false;
      
      // 브라우저 렌더링/레이아웃이 확실히 끝날 때까지 대기
      setTimeout(() => {
        const newRowInput = tableContainerRef.current?.querySelector(".selected-row input");
        if (newRowInput) {
          newRowInput.focus();
        } else {
          // 포커스를 못 잡을 경우를 완벽하게 대비해 wrapper 영역이라도 강제 포커싱
          tableContainerRef.current?.parentElement?.focus();
        }
      }, 50);
    }
  }, [selectedProductCode, validCurrentPage]);

  useEffect(() => {
    const listArr = Array.isArray(filteredList) ? filteredList : [];
    const selectedIndex = listArr.findIndex(
      (item) => item && item.ProductCode === selectedProductCode
    );

    if (selectedIndex !== -1) {
      const targetPage = Math.floor(selectedIndex / itemsPerPage) + 1;
      if (setCurrentPage) {
        setCurrentPage(targetPage);
      }
    } else {
      // 선택된 항목이 없거나 새 검색어에 맞지 않으면 1페이지로 이동
      if (setCurrentPage) {
        setCurrentPage(1);
      }
    }
  }, [keyword, selectedProductCode, filteredList]); // validCurrentPage는 제외 (사용자 임의 페이지 이동 방해 금지)

  useEffect(() => {
    let timer;
    if (slideOpen) {
      timer = setTimeout(() => {
        // 지연 랜더링 200ms 지연 후 실행
        const updatedQuantities = {};
        list.forEach((item) => {
          const matched = orderItemList.find(
            (d) => d && d.ProductCode === item?.ProductCode
          );
          updatedQuantities[item.ProductCode] = matched
            ? String(matched.Qty)
            : "0";
        });
        setQuantities(updatedQuantities);
      }, 200); // 200ms 지연 후 실행
    }

    return () => {
      if (timer) clearTimeout(timer); // cleanup
    };
  }, [slideOpen, list, orderItemList]);

  const handleFormattedInputChange = (productCode, inputValue) => {
    if (inputValue === "") {
      setQuantities({ ...quantities, [productCode]: "" });
      return;
    }
    const numericValue = parseInt(inputValue.replace(/,/g, ""), 10);
    if (!isNaN(numericValue)) {
      setQuantities({ ...quantities, [productCode]: numericValue });
    } else {
      setQuantities({ ...quantities, [productCode]: "" });
    }
  };

  const formatNumber = (value) => {
    if (value === "") return "";
    const num = parseInt(value?.toString().replace(/,/g, "") || "", 10);
    return isNaN(num) ? "" : num.toLocaleString();
  };

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const highlightText = (text, query) => {
    if (!query) return text;
    const plainText = typeof text === "string" ? text : String(text);
    const safeQuery = escapeRegExp(query); // 특수문자 처리
    const regex = new RegExp(`(${safeQuery})`, "gi");
    const testRegex = new RegExp(`^${safeQuery}$`, "i");
    const parts = plainText.split(regex);
    return parts.map((part, idx) =>
      testRegex.test(part) ? <mark key={idx}>{part}</mark> : part
    );
  };

  const handleQueryInputChange = (e) => {
    setProductQuery(e.target.value);
  };

  return (
    <>
      {/* 외부 wrapper: flex-column으로 테이블+페이지네이션 분리 */}
      <div 
        style={{ display: "flex", flexDirection: "column", height: "750px" }}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocus={(e) => (e.currentTarget.style.outline = "none")}
      >
        <div className="form-wrapper" style={{ flexShrink: 0 }}>
          <div className="input-row">
          <label className="input-label">요청사항</label>
          <textarea
            className="input-box request-textarea"
            name="requestBox"
            value={requestText}
            placeholder="요청사항을 입력하세요."
            onChange={onRequestBoxChange}
            rows={1}
          />
        </div>

        <div className="input-row">
          <label className="input-label">검&nbsp;&nbsp;&nbsp;색</label>
          <input
            className="input-box query-input"
            type="text"
            name="queryBox"
            placeholder="품목명, 모델명으로 검색하세요."
            value={productQuery}
            onChange={handleQueryInputChange}
          />
        </div>
      </div>

      <div className="table-container" ref={tableContainerRef} style={{ flex: 1, height: "auto", outline: "none", overflow: "auto" }}>
        <table className="table table-hover">
          <thead>
            <tr>
              {["품목코드", "품목명", "모델명", "수량", "단가", "작업"].map(
                (header, idx) => (
                  <th key={idx} style={{ width: columnWidths[idx] }}>
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {(currentItems || [])
              .filter((d) => d && d.ProductCode)
              .map((d) => {
                const isInOrderList = (orderItemList || []).some(
                  (item) =>
                    item &&
                    item.ProductCode &&
                    item.ProductCode === d.ProductCode
                );

                return (
                  <tr
                    key={d.ProductCode}
                    ref={selectedProductCode === d.ProductCode ? selectedRowRef : null}
                    onClick={() => handleOrderBillRowClick(d)}
                    className={
                      selectedProductCode === d.ProductCode
                        ? "selected-row"
                        : ""
                    }
                  >
                    <td style={{ textAlign: columnAligns[0] }}>
                      {highlightText(d.ProductCode, keyword)}
                    </td>
                    <td style={{ textAlign: columnAligns[1] }}>
                      {highlightText(d.AliasName, keyword)}
                    </td>
                    <td style={{ textAlign: columnAligns[2] }}>
                      {highlightText(d.ModelName, keyword)}
                    </td>
                    <td style={{ textAlign: columnAligns[3] }}>
                      {Number(d.PackingUnit) !== 0 &&
                        focusedProductCode === d.ProductCode && (
                          <div className="remain-qty-highlight">
                            포장단위: {formatNumber(d.PackingUnit)}EA
                          </div>
                        )}
                      <input
                        className="form-control"
                        type="text"
                        value={
                          (quantities[d.ProductCode] ?? d.Qty) === "0" ||
                          (quantities[d.ProductCode] ?? d.Qty) === ""
                            ? ""
                            : formatNumber(quantities[d.ProductCode] ?? d.Qty)
                        }
                        onChange={(e) =>
                          handleFormattedInputChange(
                            d.ProductCode,
                            e.target.value
                          )
                        }
                        onFocus={() => setFocusedProductCode(d.ProductCode)}
                        onBlur={() => setFocusedProductCode(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            e.preventDefault();
                            const qty = quantities[d.ProductCode] ?? 0;
                            if (qty <= 0) {
                              toast.error("수량은 1개 이상 입력해야 합니다.");
                              return;
                            }
                            const updatedItem = { ...d, Qty: qty };
                            handleAddOrderItem(updatedItem);
                          }
                        }}
                        style={{ width: columnWidths[3], textAlign: "right" }}
                      />
                    </td>

                    <td style={{ textAlign: columnAligns[4] }}>
                      {formatNumber(d.UnitPrice)}
                    </td>
                    <td style={{ textAlign: "center", padding: "0" }}>
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        <button
                          type="button"
                          className={
                            quantities[d.ProductCode] > 0
                              ? "btn-red"
                              : "btn-blue-outline"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const qty = quantities[d.ProductCode] ?? 0;
                            if (qty <= 0) {
                              toast.error("수량은 1개 이상 입력해야 합니다.");
                              return;
                            }
                            const updatedItem = { ...d, Qty: qty };
                            handleAddOrderItem(updatedItem);
                          }}
                        >
                          {isInOrderList ? "수정" : "주문"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        </div>

        <div className="pagination" style={{ flexShrink: 0 }}>
          <button onClick={handlePrevGroup} disabled={pageGroup <= 0}>
            &laquo; 이전
          </button>

          {Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, i) => {
            const pageNum = startPage + i;
            return (
              <button
                key={pageNum}
                onClick={() => goToPage(pageNum)}
                className={validCurrentPage === pageNum ? "active" : ""}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={handleNextGroup}
            disabled={pageGroup >= totalGroups - 1 || totalGroups === 0}
          >
            다음 &raquo;
          </button>
        </div>
      </div>
    </>
  );
};

export default Table_1;
